/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { submit } from 'redux-form';
import { useHistory, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import { AutoparkTransportModalContent } from './components/transport-modal-content';
import {
	createAutoparkTransportAction,
	updateAutoparkTransportAction,
	getAutoparkTransportInfoAction,
	deleteAutoparkTransportAction,
	resetStateAction,
	getTransportTypesListAction,
	getAutoparkTransportTariffTypesListAction,
	getAddressesListAction,
} from './actions';
import {
	getActiveAutoparkIdSelector,
	getActiveAutoparkTransportIdSelector,
	getAutoparkTransportInfoSelector,
	getAutoparkTransportNameSelector,
	getIsAuditTabShownSelector,
	getIsAutoparkTransportAddFormOpenSelector,
	getIsAutoparkTransportEditFormOpenSelector,
	getIsAutoparkTransportInfoLoadingSelector,
	getIsModalShownSelector,
} from './selectors';
import { getIsAvailableAutoparksTransportsButtonsSelector } from '../profile/selectors';
import { setWindowInfoAction } from '../alert-modal/actions';
import { Audit } from '../../components/audit';
import { AUTOPARK_TRANSPORT_FORM_NAME } from './constants';
import closeModal from '../../utils/closeModal';

const AutoparkTransportModalWrapper = ({
	activeAutoparkId,
	activeAutoparkTransportId,
	isAvailable,
	onGetAutoparkTransportInfo,
	isAutoparkTransportInfoLoading,
	autoparkTransportInfo,
	autoparkName,
	onResetState,
	onFormSubmit,
	onCreateAutoparkTransport,
	onUpdateAutoparkTransport,
	onDeleteAutoparkTransport,
	onSetWindowInfo,
	isModalShown,
	isAuditTabShown,
	isAutoparkTransportAddFormOpen,
	isAutoparkTransportEditFormOpen,
	onGetTransportTypesList,
	onGetAutoparkTransportTariffTypesList,
	onGetAddressesList,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'autoparkTransportModalSaga', saga });
	useInjectReducer({ key: 'autoparkTransportModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const modal = query.get('modal');

	const mode = query.get('mode');

	const history = useHistory();

	useEffect(() => {
		return () => onResetState();
	}, []);

	useEffect(() => {
		if (activeAutoparkId && activeAutoparkTransportId) {
			onGetAutoparkTransportInfo({
				autoparkId: activeAutoparkId,
				transportId: activeAutoparkTransportId,
			});
		}
	}, [activeAutoparkId, activeAutoparkTransportId]);

	useEffect(() => {
		if (isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen) {
			onGetTransportTypesList();
			onGetAutoparkTransportTariffTypesList();
			onGetAddressesList();
		}
	}, [isAutoparkTransportAddFormOpen, isAutoparkTransportEditFormOpen]);

	const handleAutoparkTransportFormOpenButtonClick = mode => {
		if (activeAutoparkTransportId) {
			query.set('transportId', activeAutoparkTransportId);
		} else {
			query.delete('transportId');
		}

		if (mode === 'edit') {
			query.set('mode', 'edit');
		}

		if (mode === 'cancel') {
			query.delete('mode');
		}

		if (mode === 'cancel' && !activeAutoparkTransportId) {
			onResetState();
			query.set('modal', 'autopark');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleModalClose = () => {
		closeModal(query);

		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleTransportModalClose = () => {
		query.delete('transportId');
		query.delete('tab');
		query.set('modal', 'autopark');

		onResetState();
		history.push(`${pathname}?${query}`);
	};

	const handleAutoparkTransportDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () =>
					onDeleteAutoparkTransport({
						autoparkId: activeAutoparkId,
						transportId: activeAutoparkTransportId,
						redirect: () => {
							query.delete('transportId');
							query.set('modal', 'autopark');
							onResetState();
							history.push(`${pathname}?${query}`);
						},
					}),
			},
		});
	};

	const handleAutoparkTransportFormSubmit = async () => {
		await onFormSubmit(AUTOPARK_TRANSPORT_FORM_NAME);

		if (activeAutoparkTransportId) {
			onUpdateAutoparkTransport({
				autoparkId: activeAutoparkId,
				transportId: activeAutoparkTransportId,
				searchQuery: query.toString(),
				redirect: () => handleAutoparkTransportFormOpenButtonClick('cancel'),
			});

			return;
		}

		onCreateAutoparkTransport({
			autoparkId: activeAutoparkId,
			searchQuery: query.toString(),
			redirect: () => handleAutoparkTransportFormOpenButtonClick('cancel'),
		});
	};

	const handleNavItemClick = modal => {
		if (modal === 'audit') {
			query.set('tab', 'audit');
		} else {
			query.set('modal', modal);
			query.delete('tab');
		}

		if (mode) {
			query.delete('mode');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleAutoparkTransportFormSubmit();
		}
	};

	return (
		<ModalWindow
			isLoading={isAutoparkTransportInfoLoading}
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			headerText={
				isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen
					? `${isAutoparkTransportEditFormOpen ? 'Изменить транспорт' : 'Создать транспорт'}`
					: autoparkName
			}
			navItems={
				isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen
					? []
					: [
							{
								id: 'info',
								text: 'Информация',
								onClick: () => handleNavItemClick('transport'),
								isActive: modal === 'transport' && !isAuditTabShown,
							},
							{
								id: 'audit',
								text: 'Изменения',
								onClick: () => handleNavItemClick('audit'),
								isActive: isAuditTabShown,
							},
					  ]
			}
			modalContent={
				!isAuditTabShown ? (
					<AutoparkTransportModalContent
						autoparkTransportInfo={autoparkTransportInfo}
						isAutoparkTransportInfoLoading={isAutoparkTransportInfoLoading}
						isAutoparkTransportFormOpen={isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen}
					/>
				) : (
					<Audit
						entityName={`autoparks/${activeAutoparkId}/transports`}
						entityId={activeAutoparkTransportId}
					/>
				)
			}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'profile' }]}
			footerButtons={
				<ModalFooterButtons
					leftButtons={
						!(isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen) &&
						isAvailable.update &&
						!isAuditTabShown
							? [
									{
										onClick: handleAutoparkTransportDelete,
										text: 'Удалить',
										color: 'warning',
										disabled: isAutoparkTransportInfoLoading,
									},
							  ]
							: []
					}
					rightButtons={
						isAutoparkTransportEditFormOpen || isAutoparkTransportAddFormOpen
							? [
									{
										onClick: handleAutoparkTransportFormSubmit,
										text: 'Сохранить',
										color: 'primary',
										loading: isAutoparkTransportInfoLoading,
										disabled: isAutoparkTransportInfoLoading,
									},
									{
										onClick: () => handleAutoparkTransportFormOpenButtonClick('cancel'),
										text: 'Отмена',
										color: 'secondary',
										disabled: isAutoparkTransportInfoLoading,
									},
							  ]
							: [
									...(isAvailable.update && !isAuditTabShown
										? [
												{
													onClick: () => handleAutoparkTransportFormOpenButtonClick('edit'),
													text: 'Изменить',
													color: 'primary',
													disabled: isAutoparkTransportInfoLoading,
												},
										  ]
										: []),
									{
										onClick: () => handleTransportModalClose(),
										text: 'Закрыть',
										color: 'secondary',
									},
							  ]
					}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	autoparkTransportInfo: getAutoparkTransportInfoSelector(),
	isAutoparkTransportInfoLoading: getIsAutoparkTransportInfoLoadingSelector(),
	autoparkName: getAutoparkTransportNameSelector(),
	isModalShown: getIsModalShownSelector(),
	activeAutoparkId: getActiveAutoparkIdSelector(),
	activeAutoparkTransportId: getActiveAutoparkTransportIdSelector(),
	isAvailable: getIsAvailableAutoparksTransportsButtonsSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isAutoparkTransportAddFormOpen: getIsAutoparkTransportAddFormOpenSelector(),
	isAutoparkTransportEditFormOpen: getIsAutoparkTransportEditFormOpenSelector(),
});

const mapDispatchToProps = {
	onGetAutoparkTransportInfo: getAutoparkTransportInfoAction,
	onDeleteAutoparkTransport: deleteAutoparkTransportAction,
	onResetState: resetStateAction,
	onFormSubmit: submit,
	onCreateAutoparkTransport: createAutoparkTransportAction,
	onUpdateAutoparkTransport: updateAutoparkTransportAction,
	onSetWindowInfo: setWindowInfoAction,
	onGetTransportTypesList: getTransportTypesListAction,
	onGetAutoparkTransportTariffTypesList: getAutoparkTransportTariffTypesListAction,
	onGetAddressesList: getAddressesListAction,
};

const AutoparkTransportModal = connect(mapStateToProps, mapDispatchToProps)(AutoparkTransportModalWrapper);

AutoparkTransportModalWrapper.propTypes = {
	onGetAutoparkTransportInfo: PropTypes.func.isRequired,
	onDeleteAutoparkTransport: PropTypes.func.isRequired,
	autoparkTransportInfo: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]).isRequired,
			group: PropTypes.string,
		}),
	).isRequired,
	isAutoparkTransportInfoLoading: PropTypes.bool.isRequired,
	autoparkName: PropTypes.string.isRequired,
	onResetState: PropTypes.func.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	onCreateAutoparkTransport: PropTypes.func.isRequired,
	onUpdateAutoparkTransport: PropTypes.func.isRequired,
	isModalShown: PropTypes.bool.isRequired,
	activeAutoparkId: PropTypes.string,
	activeAutoparkTransportId: PropTypes.string,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	onSetWindowInfo: PropTypes.func.isRequired,
	isAuditTabShown: PropTypes.bool.isRequired,
	isAutoparkTransportAddFormOpen: PropTypes.bool.isRequired,
	isAutoparkTransportEditFormOpen: PropTypes.bool.isRequired,
	onGetTransportTypesList: PropTypes.func.isRequired,
	onGetAutoparkTransportTariffTypesList: PropTypes.func.isRequired,
	onGetAddressesList: PropTypes.func.isRequired,
};

export default AutoparkTransportModal;
